/**
 * TreasureListFields — the editable body of a {@link TrlTreasureConfig}.
 *
 * Authors the game's reward pool: each row is one reward (credits / augments /
 * a SWTOR item from the platform catalogue / a custom prize), tagged with a quality tier and a
 * quantity (copies distributed independently across the map).
 *
 * The "what is this reward" part of each row (the item picker, the custom title/icon fields)
 * comes from {@link TreasureRewardIdentityFields}, shared with the consolation pool editor.
 *
 * Below the pool sit the three tier curves. A tier's spread (sigma) controls how
 * hard it clusters on its target round — 'high' aims at the last round, 'low' at
 * the first — and the live bar preview is drawn from the SAME function the
 * re-roll uses (`trlTreasureRoundProbabilities`), so what an admin sees is
 * literally the weighting they are about to roll against.
 *
 * Purely controlled, mirroring StalemateConfigFields / RadarConfigFields.
 */

import { useMemo } from 'react';
import {
  TRL_TREASURE_TIERS,
  TRL_TREASURE_TIER_LABELS,
  TRL_TREASURE_KINDS,
  TRL_TREASURE_KIND_LABELS,
  TRL_TREASURE_MAX_ITEMS,
  TRL_TREASURE_MAX_QUANTITY,
  TRL_TREASURE_MIN_SPREAD,
  TRL_TREASURE_MAX_SPREAD,
  trlTreasureRoundProbabilities,
  type TrlTreasureConfig,
  type TrlTreasureItem,
  type TrlTreasureKind,
  type TrlTreasureTier,
  type TrlDistributableNode,
} from '@swtorbit/shared-socket-events';
import { TreasureRewardIdentityFields } from './TreasureRewardIdentityFields';

interface TreasureListFieldsProps {
  value: TrlTreasureConfig;
  onChange: (next: TrlTreasureConfig) => void;
  /** Current map nodes — drives the curve preview's round axis. */
  nodes: TrlDistributableNode[];
  disabled?: boolean;
}

/** A fresh item of the given kind, with sensible starting values. */
function blankItem(kind: TrlTreasureKind): TrlTreasureItem {
  const base = { id: crypto.randomUUID(), tier: 'mid' as TrlTreasureTier, quantity: 1 };
  switch (kind) {
    case 'credits':
      return { ...base, kind: 'credits', amount: 100000 };
    case 'augment':
      return { ...base, kind: 'augment', amount: 1 };
    case 'cartel':
      // No item yet: the row's picker asks for one (a reward left without one is dropped on save).
      return { ...base, kind: 'cartel' };
    case 'custom':
      return { ...base, kind: 'custom', title: 'New reward' };
  }
}

export function TreasureListFields({ value, onChange, nodes, disabled = false }: TreasureListFieldsProps) {
  const items = value.items;

  // Round-axis probabilities per tier, recomputed as the map or spreads change.
  const curves = useMemo(
    () =>
      TRL_TREASURE_TIERS.map((tier) => ({
        tier,
        probs: trlTreasureRoundProbabilities(tier, nodes, value.tierSpread),
      })),
    [nodes, value.tierSpread],
  );

  const patchItem = (id: string, patch: Partial<TrlTreasureItem>) => {
    onChange({
      ...value,
      // The cast is needed because a partial patch can't prove it keeps the
      // discriminated union intact; every call site patches within one kind.
      items: items.map((it) => (it.id === id ? ({ ...it, ...patch } as TrlTreasureItem) : it)),
    });
  };

  /** Switching kind rebuilds the row, preserving tier + quantity. */
  const changeKind = (id: string, kind: TrlTreasureKind) => {
    const existing = items.find((it) => it.id === id);
    if (!existing) return;
    const replacement = blankItem(kind);
    onChange({
      ...value,
      items: items.map((it) =>
        it.id === id ? { ...replacement, id: existing.id, tier: existing.tier, quantity: existing.quantity } : it,
      ),
    });
  };

  const removeItem = (id: string) => {
    onChange({ ...value, items: items.filter((it) => it.id !== id) });
  };

  const addItem = () => {
    if (items.length >= TRL_TREASURE_MAX_ITEMS) return;
    onChange({ ...value, items: [...items, blankItem('credits')] });
  };

  const setSpread = (tier: TrlTreasureTier, spread: number) => {
    onChange({ ...value, tierSpread: { ...value.tierSpread, [tier]: spread } });
  };

  const totalPlacements = items.reduce((sum, it) => sum + Math.max(1, it.quantity), 0);
  const eligibleNodeCount = nodes.filter((n) => (n.derivedLevel ?? 0) >= 1).length;

  return (
    <div className="d-flex flex-column gap-xs">
      <span className="fs-xs text-uppercase text-slate">Treasure list</span>
      <span className="fs-xs text-slate">
        Rewards the first team to clear a planet wins. Each item&apos;s tier biases which round it
        tends to land in when the distribution is rolled — it is a bias, not a guarantee.
      </span>

      {items.length === 0 ? (
        <p className="text-slate fs-sm m-0" style={{ padding: '1rem 0' }}>
          No treasure yet. Add an item to get started.
        </p>
      ) : (
        items.map((item) => (
          <div key={item.id} className="border-md border-blue-light rounded-sm p-xs d-flex flex-column gap-2xs">
            <div className="d-flex gap-2xs align-items-center flex-wrap">
              <select
                className="input input--sm"
                value={item.kind}
                onChange={(e) => changeKind(item.id, e.target.value as TrlTreasureKind)}
                disabled={disabled}
                aria-label="Reward type"
              >
                {TRL_TREASURE_KINDS.map((k) => (
                  <option key={k} value={k}>{TRL_TREASURE_KIND_LABELS[k]}</option>
                ))}
              </select>

              <select
                className="input input--sm"
                value={item.tier}
                onChange={(e) => patchItem(item.id, { tier: e.target.value as TrlTreasureTier })}
                disabled={disabled}
                aria-label="Quality tier"
                title="Biases which round this reward tends to land in"
              >
                {TRL_TREASURE_TIERS.map((t) => (
                  <option key={t} value={t}>{TRL_TREASURE_TIER_LABELS[t]}</option>
                ))}
              </select>

              <label className="fs-xs text-slate d-flex align-items-center gap-2xs m-0">
                ×
                <input
                  type="number"
                  className="input input--sm"
                  style={{ width: '4.5rem' }}
                  min={1}
                  max={TRL_TREASURE_MAX_QUANTITY}
                  value={item.quantity}
                  onChange={(e) => patchItem(item.id, { quantity: Number(e.target.value) })}
                  disabled={disabled}
                  title="Copies placed independently across the map"
                />
              </label>

              <button
                type="button"
                className="btn btn--outline btn--slate btn--sm"
                style={{ marginLeft: 'auto' }}
                onClick={() => removeItem(item.id)}
                disabled={disabled}
              >
                Remove
              </button>
            </div>

            {/* Kind-specific fields */}
            {(item.kind === 'credits' || item.kind === 'augment') && (
              <input
                type="number"
                className="input input--sm"
                min={0}
                value={item.amount}
                onChange={(e) => patchItem(item.id, { amount: Number(e.target.value) })}
                disabled={disabled}
                placeholder={item.kind === 'credits' ? 'Credits' : 'Augments'}
                aria-label={item.kind === 'credits' ? 'Credit amount' : 'Augment amount'}
              />
            )}

            <TreasureRewardIdentityFields
              kind={item.kind}
              value={item}
              onChange={(patch) => patchItem(item.id, patch as Partial<TrlTreasureItem>)}
              disabled={disabled}
            />
          </div>
        ))
      )}

      <div className="d-flex align-items-center gap-xs">
        <button
          type="button"
          className="btn btn--outline btn--slate btn--sm"
          onClick={addItem}
          disabled={disabled || items.length >= TRL_TREASURE_MAX_ITEMS}
        >
          + Add treasure
        </button>
        {items.length > 0 && (
          <span className="fs-xs text-slate">
            {totalPlacements} placement{totalPlacements === 1 ? '' : 's'} across {eligibleNodeCount} planet
            {eligibleNodeCount === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {/* ── Tier curves ─────────────────────────────────────────────────── */}
      <div className="border-md border-blue-light rounded-sm p-xs d-flex flex-column gap-xs mt-xs">
        <span className="fs-xs text-uppercase text-slate">Tier curves</span>
        <span className="fs-xs text-slate">
          How tightly each tier clusters on its target round. Widen a curve to flatten the bias
          toward an even spread.
        </span>

        {curves.map(({ tier, probs }) => (
          <div key={tier} className="d-flex flex-column gap-2xs">
            <div className="d-flex align-items-center gap-xs">
              <span className="fs-xs" style={{ minWidth: '3.5rem' }}>{TRL_TREASURE_TIER_LABELS[tier]}</span>
              <input
                type="range"
                className="range flex-1"
                min={TRL_TREASURE_MIN_SPREAD}
                max={TRL_TREASURE_MAX_SPREAD}
                step={0.25}
                value={value.tierSpread[tier]}
                onChange={(e) => setSpread(tier, Number(e.target.value))}
                disabled={disabled}
                aria-label={`${TRL_TREASURE_TIER_LABELS[tier]} tier spread`}
              />
              <span className="fs-xs text-slate" style={{ minWidth: '2.5rem', textAlign: 'right' }}>
                {value.tierSpread[tier].toFixed(2)}
              </span>
            </div>

            {probs.length > 0 ? (
              <div className="trl-treasure-curve" title={`Chance of landing in each round (${TRL_TREASURE_TIER_LABELS[tier]})`}>
                {probs.map((p, i) => (
                  <div
                    key={i}
                    className="trl-treasure-curve__bar"
                    // Scale against the peak so a flat curve is still visible.
                    style={{ height: `${Math.round((p / Math.max(...probs)) * 100)}%` }}
                    title={`Round ${i + 1}: ${(p * 100).toFixed(1)}%`}
                  />
                ))}
              </div>
            ) : (
              <span className="fs-xs text-slate">Connect the map to preview the curve.</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
